import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { CheckCircle2, AlertTriangle, RefreshCw, ShieldCheck } from "lucide-react";

/**
 * AdminContinuityPanel — Continuity & IP-Integrity console.
 *
 * Surfaces the post-deploy self-test (services/post_deploy_verify.py) +
 * the admin-seed snapshot inside the admin dashboard so the owner has
 * a one-glance view of "is production healthy right now?". Replaces
 * the public diagnostic endpoints, which are now gated off in prod.
 */
export function AdminContinuityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/continuity/status");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const rerun = async () => {
    setRerunning(true);
    setError(null);
    try {
      const r = await api.post("/admin/continuity/rerun");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Rerun failed");
    } finally {
      setRerunning(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div data-testid="adm-cont-loading" className="text-cocoa/60 text-sm">Loading…</div>;
  if (error)   return <div data-testid="adm-cont-error" className="text-red-700 text-sm">{error}</div>;
  if (!data)   return null;

  const seed = data.admin_seed || {};
  const pd = data.post_deploy || {};
  const overallOk = pd.all_ok && (seed.admin_count || 0) > 0;

  return (
    <div data-testid="adm-continuity-panel" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Continuity & IP Integrity</p>
          <h2 className="font-heading text-2xl text-cocoa mt-1">
            {overallOk ? "Production healthy." : "Attention required."}
          </h2>
          {pd.ran_at && (
            <p className="text-xs text-cocoa/55 mt-1">
              Last verified: <span className="font-mono">{new Date(pd.ran_at).toLocaleString()}</span>
            </p>
          )}
        </div>
        <button
          data-testid="adm-cont-rerun"
          onClick={rerun}
          disabled={rerunning}
          className="ss-pill ss-pill-ink h-9 px-4 text-sm disabled:opacity-50"
        >
          <RefreshCw size={14} className={rerunning ? "animate-spin" : ""} />
          {rerunning ? "Re-running…" : "Re-run self-test"}
        </button>
      </div>

      {/* Self-test checks */}
      <div className="bg-ivory border border-champagne rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-champagne bg-champagne/20">
          <p className="text-[11px] tracking-[0.3em] uppercase text-cocoa/70 flex items-center gap-2">
            <ShieldCheck size={14} /> Post-deploy self-test
          </p>
        </div>
        <ul className="divide-y divide-champagne/60">
          {(pd.checks || []).map((c) => (
            <li key={c.name} data-testid={`adm-cont-check-${c.name}`} className="px-4 py-3 flex items-start gap-3">
              {c.ok ? (
                <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm text-cocoa font-medium">{c.name}</p>
                {!c.ok && c.details && (
                  <pre className="mt-1 text-[11px] text-cocoa/60 bg-champagne/20 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
                    {JSON.stringify(c.details, null, 2)}
                  </pre>
                )}
              </div>
            </li>
          ))}
          {(!pd.checks || pd.checks.length === 0) && (
            <li className="px-4 py-3 text-sm text-cocoa/55">No verification record yet — click Re-run.</li>
          )}
        </ul>
      </div>

      {/* Admin seed snapshot */}
      <div className="bg-ivory border border-champagne rounded-lg p-4">
        <p className="text-[11px] tracking-[0.3em] uppercase text-cocoa/70">Admin seed snapshot</p>
        <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
          <div>
            <p className="text-cocoa/55 text-xs">DB</p>
            <p className="font-mono text-cocoa">{seed.db_name || "—"}</p>
          </div>
          <div>
            <p className="text-cocoa/55 text-xs">Admin count</p>
            <p data-testid="adm-cont-admin-count" className="font-mono text-cocoa">{seed.admin_count ?? "—"}</p>
          </div>
          <div>
            <p className="text-cocoa/55 text-xs">Total users</p>
            <p className="font-mono text-cocoa">{seed.total_user_count ?? "—"}</p>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-cocoa/55 text-xs">Admins</p>
          <ul className="mt-1 space-y-1">
            {(seed.admins || []).map((a) => (
              <li key={a.email} className="text-sm font-mono text-cocoa">
                {a.email}
                {a.created_at && <span className="text-cocoa/45 text-xs ml-2">· created {new Date(a.created_at).toLocaleDateString()}</span>}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-cocoa/45 mt-4">
          Expected: {(seed.expected_admin_emails || []).join(", ")}
        </p>
      </div>
    </div>
  );
}

export default AdminContinuityPanel;
